import { useProvince } from '../hooks/useProvince';
import { Spinner } from '../components/Spinner';
import { ErrorMessage } from '../components/ErrorMessage';
import { Button } from '../components/ui/Button';
import { useAppShellPlatform } from '../hooks/useAppShellPlatform';
import { MobileThroneShell } from '../components/throne/MobileThroneShell';
import { DesktopThroneShell } from '../components/throne/DesktopThroneShell';

/**
 * Throne Room - Province Dashboard (SF-66, redesigned SF-122)
 *
 * A thin platform switch: `useAppShellPlatform()` selects `MobileThroneShell` or
 * `DesktopThroneShell`, both consuming the same `useProvince()` data — no `ProvinceDto` changes.
 * Retains its three existing top-level guards (loading / error+retry / not-found) unchanged,
 * ahead of the platform switch — both shells can assume `province` is always populated and never
 * re-implement these guards themselves (Phase 2 C10).
 */
export function ThroneRoom() {
  const { province, isLoading, error, refresh } = useProvince();
  const platform = useAppShellPlatform();

  if (isLoading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <ErrorMessage message={error} />
          <Button onClick={refresh} className="mt-4 w-full">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!province) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <ErrorMessage message="Province not found" />
        </div>
      </div>
    );
  }

  return platform === 'mobile'
    ? <MobileThroneShell province={province} />
    : <DesktopThroneShell province={province} />;
}
